/**
 * Unify Patient — extends FHIR R4 Patient.
 *
 * Represents therapy clients. The Patient resource is the primary clinical
 * identity for clients and the login profile for client portal users.
 * Family/couples participants are contact-only RelatedPerson resources, not Patients.
 *
 * ## Standard FHIR fields used by Unify
 *
 * | Field | Usage |
 * |-------|-------|
 * | `name[0]` (use: `official`) | Legal name (given + family) |
 * | `name[1]` (use: `nickname`) | Preferred / display name (optional) |
 * | `telecom[]` | Email (`system: 'email'`, `use: 'home'`), phone (`system: 'phone'`, `use: 'mobile'`) |
 * | `identifier[]` | Seed tag identifier, email identifier |
 * | `gender` | Administrative gender (`male`, `female`, `other`) |
 * | `birthDate` | Date of birth (ISO date) |
 * | `active` | `true` when the client has ≥1 active EpisodeOfCare |
 * | `photo[0]` | Profile photo (Attachment) |
 * | `address[0]` | Home address |
 * | `communication[]` | Languages spoken |
 * | `contact[0]` | Emergency contact (name, relationship, phone, email) |
 * | `link[]` | References to `RelatedPerson` resources (`type: 'seealso'`) for family/couples therapy |
 *
 * ## Custom extensions
 *
 * | Extension URL | Value type | Purpose |
 * |---------------|------------|---------|
 * | `EXT_URLS.role` | `valueString` (`'client'`) | RBAC role — always `'client'` for Patient profiles |
 * | `EXT_URLS.uiPermissions` | `valueString` (JSON) | Resolved UI permission flags |
 * | `EXT_URLS.uiPermissionOverrides` | `valueString` (JSON) | Per-user permission overrides |
 * | `EXT_URLS.portalSubjectPatient` | `valueReference` → Patient | Legacy portal-switching pointer (UI only; deferred, no guarantor/proxy login exists) |
 * | `EXT_URLS.portalRelatedPatient` | `valueReference` → Patient | Linked child/dependent Patient for parent portal |
 * | `EXT_URLS.pronouns` | `valueString` | Display pronouns (e.g. she/her, he/him, they/them) |
 * | `ADMIN_EXT_URLS.organization` | `valueReference` → Organization | Staff practice membership (not clients) |
 * | `GENDER_IDENTITY_EXT` | `valueString` | Gender identity |
 * | `LGBTQIA_IDENTITIES_EXT` | `valueString` | LGBTQIA+ identity |
 * | `RELIGION_SPIRITUALITY_EXT` | `valueString` | Religion / spirituality |
 * | `RACE_ETHNICITY_EXT` | `valueString` (JSON array) | Race / ethnicity |
 * | `BIO_EXT` | `valueString` | Self-service profile biography |
 * | `ALIASES_EXT` | `valueString` (JSON array) | Other names / aliases |
 * | `CLINICAL_EXT` | `valueString` (JSON) | Self-service profile details and demographics |
 */
import type { Patient, Reference, Extension } from '@medplum/fhirtypes';

import {
  EXT_URLS,
  ADMIN_EXT_URLS,
  GENDER_IDENTITY_EXT,
  LGBTQIA_IDENTITIES_EXT,
  RELIGION_SPIRITUALITY_EXT,
  RACE_ETHNICITY_EXT,
  NOTIFICATION_PREFERENCES_EXT,
  PATIENT_IS_HIGH_RISK_EXT,
} from '../constants';

// ─── Private helpers ────────────────────────────────────────────────

function getExt(resource: Patient, url: string): Extension | undefined {
  return resource.extension?.find((e) => e.url === url);
}

function setExt(resource: Patient, url: string, ext: Partial<Extension>): Patient {
  const extensions = (resource.extension ?? []).filter((e) => e.url !== url);
  extensions.push({ url, ...ext } as Extension);
  return { ...resource, extension: extensions };
}

function removeExt(resource: Patient, url: string): Patient {
  return { ...resource, extension: (resource.extension ?? []).filter((e) => e.url !== url) };
}

/** Two-letter jurisdiction from the canonical home address, falling back to the first address. */
export function getPatientHomeState(resource: Patient): string | undefined {
  const state = (resource.address?.find((address) => address.use === 'home') ?? resource.address?.[0])?.state?.trim();
  return state ? state.toUpperCase() : undefined;
}

// ─── Role ───────────────────────────────────────────────────────────

export function getPatientRole(resource: Patient): string {
  return getExt(resource, EXT_URLS.role)?.valueString ?? 'client';
}

export function setPatientRole(resource: Patient, role: string): Patient {
  return setExt(resource, EXT_URLS.role, { valueString: role });
}

// ─── UI permissions ─────────────────────────────────────────────────

export function getPatientUiPermissions(resource: Patient): Record<string, boolean> | undefined {
  const raw = getExt(resource, EXT_URLS.uiPermissions)?.valueString;
  return raw ? (JSON.parse(raw) as Record<string, boolean>) : undefined;
}

export function setPatientUiPermissions(resource: Patient, perms: Record<string, boolean>): Patient {
  return setExt(resource, EXT_URLS.uiPermissions, { valueString: JSON.stringify(perms) });
}

// ─── UI permission overrides ────────────────────────────────────────

export function getPatientUiPermissionOverrides(resource: Patient): Record<string, boolean> | undefined {
  const raw = getExt(resource, EXT_URLS.uiPermissionOverrides)?.valueString;
  return raw ? (JSON.parse(raw) as Record<string, boolean>) : undefined;
}

export function setPatientUiPermissionOverrides(resource: Patient, overrides: Record<string, boolean>): Patient {
  return setExt(resource, EXT_URLS.uiPermissionOverrides, { valueString: JSON.stringify(overrides) });
}

// ─── Organization (practice membership) ─────────────────────────────

export function getPatientOrganizationRef(resource: Patient): Reference | undefined {
  return getExt(resource, ADMIN_EXT_URLS.organization)?.valueReference;
}

/** @deprecated Prefer getPatientOrganizationRef */
export const getOrganizationRef = getPatientOrganizationRef;

export function setPatientOrganizationRef(resource: Patient, ref: Reference): Patient {
  return setExt(resource, ADMIN_EXT_URLS.organization, { valueReference: ref });
}

// ─── Portal subject patient (legacy UI portal switching; deferred) ──

export function getPortalSubjectPatientRef(resource: Patient): Reference | undefined {
  return getExt(resource, EXT_URLS.portalSubjectPatient)?.valueReference;
}

export function setPortalSubjectPatientRef(resource: Patient, ref: Reference): Patient {
  return setExt(resource, EXT_URLS.portalSubjectPatient, { valueReference: ref });
}

export function removePortalSubjectPatient(resource: Patient): Patient {
  return removeExt(resource, EXT_URLS.portalSubjectPatient);
}

// ─── Portal related patient (parent → child linkage) ────────────────

export function getPortalRelatedPatientRef(resource: Patient): Reference | undefined {
  return getExt(resource, EXT_URLS.portalRelatedPatient)?.valueReference;
}

export function setPortalRelatedPatientRef(resource: Patient, ref: Reference): Patient {
  return setExt(resource, EXT_URLS.portalRelatedPatient, { valueReference: ref });
}

export function removePortalRelatedPatient(resource: Patient): Patient {
  return removeExt(resource, EXT_URLS.portalRelatedPatient);
}

// ─── Pronouns ───────────────────────────────────────────────────────

export function getPronouns(resource: Patient): string | undefined {
  return getExt(resource, EXT_URLS.pronouns)?.valueString;
}

export function setPronouns(resource: Patient, pronouns: string): Patient {
  return setExt(resource, EXT_URLS.pronouns, { valueString: pronouns });
}

// ─── Demographics: gender identity ──────────────────────────────────

export function getGenderIdentity(resource: Patient): string | undefined {
  return getExt(resource, GENDER_IDENTITY_EXT)?.valueString;
}

export function setGenderIdentity(resource: Patient, value: string): Patient {
  return setExt(resource, GENDER_IDENTITY_EXT, { valueString: value });
}

// ─── Demographics: LGBTQIA+ identity ────────────────────────────────

export function getLgbtqiaIdentities(resource: Patient): string | undefined {
  return getExt(resource, LGBTQIA_IDENTITIES_EXT)?.valueString;
}

export function setLgbtqiaIdentities(resource: Patient, value: string): Patient {
  return setExt(resource, LGBTQIA_IDENTITIES_EXT, { valueString: value });
}

// ─── Demographics: race / ethnicity ─────────────────────────────────

export function getRaceEthnicity(resource: Patient): string | undefined {
  return getExt(resource, RACE_ETHNICITY_EXT)?.valueString;
}

export function setRaceEthnicity(resource: Patient, value: string): Patient {
  return setExt(resource, RACE_ETHNICITY_EXT, { valueString: value });
}

// ─── Demographics: religion / spirituality ──────────────────────────

export function getReligionSpirituality(resource: Patient): string | undefined {
  return getExt(resource, RELIGION_SPIRITUALITY_EXT)?.valueString;
}

export function setReligionSpirituality(resource: Patient, value: string): Patient {
  return setExt(resource, RELIGION_SPIRITUALITY_EXT, { valueString: value });
}

// ─── High-risk flag (intake screening) ──────────────────────────────

export function getPatientIsHighRisk(resource: Patient): boolean {
  return getExt(resource, PATIENT_IS_HIGH_RISK_EXT)?.valueBoolean === true;
}

export function setPatientIsHighRisk(resource: Patient, value: boolean): Patient {
  return setExt(resource, PATIENT_IS_HIGH_RISK_EXT, { valueBoolean: value });
}

// ─── Notification preferences ───────────────────────────────────────

export function getPatientNotificationPreferences<T>(resource: Patient): T | undefined {
  const raw = getExt(resource, NOTIFICATION_PREFERENCES_EXT)?.valueString;
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

export function setPatientNotificationPreferences<T>(resource: Patient, prefs: T): Patient {
  return setExt(resource, NOTIFICATION_PREFERENCES_EXT, { valueString: JSON.stringify(prefs) });
}
